/**
 * Admin Feature Overrides Hooks
 * მომხმარებლის ლიმიტების მართვის ჰუკები
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getFeatureCodes,
  getUserFeatureLimits,
  getUserOverrides,
  createFeatureOverride,
  updateFeatureOverride,
  deleteFeatureOverride,
  CreateFeatureOverrideRequest,
  UpdateFeatureOverrideRequest,
} from '../features/api/adminFeatureOverrides';

// Get all available feature codes
export function useFeatureCodes() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['featureCodes'],
    queryFn: getFeatureCodes,
    staleTime: 5 * 60 * 1000, // 5 minutes - feature codes rarely change
  });

  return {
    featureCodes: data || [],
    isLoading,
    error,
  };
}

// Get user's feature limits (plan defaults + overrides)
export function useUserFeatureLimits(userId: string | null) {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['userFeatureLimits', userId],
    queryFn: () => getUserFeatureLimits(userId!),
    enabled: !!userId,
  });

  return {
    limits: data,
    isLoading,
    error,
    refetch,
  };
}

// Get user's overrides only
export function useUserOverrides(userId: string | null) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['userOverrides', userId],
    queryFn: () => getUserOverrides(userId!),
    enabled: !!userId,
  });

  return {
    overrides: data || [],
    isLoading,
    error,
  };
}

// Create feature override
export function useCreateFeatureOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, data }: { userId: string; data: CreateFeatureOverrideRequest }) =>
      createFeatureOverride(userId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userFeatureLimits', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['userOverrides', variables.userId] });
    },
  });
}

// Update feature override
export function useUpdateFeatureOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, featureCode, data }: { userId: string; featureCode: string; data: UpdateFeatureOverrideRequest }) =>
      updateFeatureOverride(userId, featureCode, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userFeatureLimits', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['userOverrides', variables.userId] });
    },
  });
}

// Delete feature override
export function useDeleteFeatureOverride() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, featureCode }: { userId: string; featureCode: string }) =>
      deleteFeatureOverride(userId, featureCode),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['userFeatureLimits', variables.userId] });
      queryClient.invalidateQueries({ queryKey: ['userOverrides', variables.userId] });
    },
  });
}
